import React, { useState, useEffect } from 'react';
import API from '../services/api';

function AdminPayrollPage() {
  const [therapistId, setTherapistId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // For display stats
  const [clientCount, setClientCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);

  // We'll show the “totalCollected” (all daily logs + bonus)
  // and the “therapistPay” (split only on daily logs) in the preview
  const [totalCollected, setTotalCollected] = useState(0);
  const [therapistPay, setTherapistPay] = useState(0);

  // The combined rows (sessions + bonuses) from preview
  const [paidSessions, setPaidSessions] = useState([]);

  // Existing payroll summaries
  const [payrolls, setPayrolls] = useState([]);
  const [therapists, setTherapists] = useState([]);

  useEffect(() => {
    fetchTherapists();
    fetchPayrolls();
  }, []);

  const fetchTherapists = async () => {
    try {
      const res = await API.get('/admin/users');
      const list = res.data.filter((u) => u.role === 'THERAPIST');
      setTherapists(list);
    } catch (error) {
      console.error(error);
      alert('Error fetching therapists');
    }
  };

  const fetchPayrolls = async () => {
    try {
      const res = await API.get('/admin/payroll');
      setPayrolls(res.data);
    } catch (err) {
      console.error(err);
      alert('Error fetching payroll summaries');
    }
  };

  /**
   * PREVIEW:
   * 1) get daily-logs stats (clientCount, sessionCount)
   * 2) get union of sessions+bonuses
   * 3) fetch therapist_goals paySplit
   * 4) separate daily logs from bonus so we only do paySplit on logs
   */
  const previewPayroll = async () => {
    if (!therapistId || !startDate || !endDate) {
      alert('Please select a therapist, start date, and end date.');
      return;
    }
    try {
      // 1) Summaries for daily logs only
      const sumRes = await API.get('/admin/payroll/report', {
        params: { therapistId, start: startDate, end: endDate },
      });
      setClientCount(sumRes.data.clientCount);
      setSessionCount(sumRes.data.sessionCount);

      // 2) Combined session+bonus details
      const sesRes = await API.get('/admin/payroll/sessions', {
        params: { therapistId, startDate, endDate },
      });
      const items = sesRes.data.sessions || [];
      setPaidSessions(items);

      // 3) Get therapist's pay split from therapist_goals
      const goalsRes = await API.get(`/admin/therapist-goals/${therapistId}`);
      const paySplit = parseFloat(goalsRes.data.pay_split_percent || 50);

      // 4) Separate daily logs vs bonus lines
      let dailyLogsSum = 0;
      let bonusSum = 0;
      items.forEach((row) => {
        const collected = parseFloat(row.collected_amount) || 0;
        if (row.record_type === 'APPROVED_BONUS') {
          // This is a bonus row
          bonusSum += collected;
        } else {
          // This is a normal daily-log session
          dailyLogsSum += collected;
        }
      });

      // totalCollected = logs + bonus
      const combinedTotal = dailyLogsSum + bonusSum;
      setTotalCollected(combinedTotal);

      // therapistPay = paySplit( logs ) + full bonus
      const calcTherapistPay = dailyLogsSum * (paySplit / 100) + bonusSum;
      setTherapistPay(calcTherapistPay);

    } catch (err) {
      console.error(err);
      alert('Error previewing payroll');
    }
  };

  /**
   * GENERATE:
   * We'll still send the union array to the server.
   * The server will do the same dailyLog/bonus logic before saving.
   */
  const generatePayroll = async () => {
    if (!therapistId || !startDate || !endDate) {
      alert('Please fill all fields.');
      return;
    }
    try {
      // Use the unioned sessions + the total we computed
      const res = await API.post('/admin/payroll/generate', {
        therapistId,
        startDate,
        endDate,
        sessions: paidSessions,
        // We'll pass the "all-in" totalCollected, but the server
        // will do final logic ignoring the split on bonuses.
        totalCollected,
      });
      alert(
        `Payroll ID: ${res.data.payrollId} created.\n`
        + `Total collected = $${res.data.totalCollected}`
      );
      fetchPayrolls();
    } catch (err) {
      console.error(err);
      alert('Error generating payroll');
    }
  };

  // Approve an existing payroll
  const approvePayroll = async (payrollId) => {
    try {
      await API.put(`/admin/payroll/approve/${payrollId}`);
      alert('Payroll approved');
      fetchPayrolls();
    } catch (err) {
      console.error(err);
      alert('Error approving payroll');
    }
  };

  // Delete an existing payroll
  const deletePayroll = async (payrollId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await API.delete(`/admin/payroll/${payrollId}`);
      alert('Payroll deleted!');
      fetchPayrolls();
    } catch (err) {
      console.error(err);
      alert('Error deleting payroll');
    }
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Admin Payroll</h2>

      {/* Therapist selection */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Therapist:</label>
        <br />
        <select
          value={therapistId}
          onChange={(e) => setTherapistId(e.target.value)}
        >
          <option value="">-- Select Therapist --</option>
          {therapists.map((t) => (
            <option key={t.id} value={t.id}>
              {t.first_name} {t.last_name}
            </option>
          ))}
        </select>
      </div>

      {/* Date range */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Start Date:</label>
        <br />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label>End Date:</label>
        <br />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      {/* Buttons */}
      <button onClick={previewPayroll} style={{ marginRight: '1rem' }}>
        Preview
      </button>
      <button onClick={generatePayroll}>Generate Payroll</button>

      {/* Summary stats */}
      <div style={{ marginTop: '1rem' }}>
        <p>Clients Seen (Daily Logs Only): {clientCount}</p>
        <p>Sessions (Daily Logs Only): {sessionCount}</p>
        <p>Total Collected (Logs + Bonuses): ${totalCollected.toFixed(2)}</p>
        <p>Projected Therapist Pay (No split on bonus):
           ${therapistPay.toFixed(2)}</p>
      </div>

      {/* PREVIEWED SESSIONS & BONUSES */}
      <div style={{ marginTop: '2rem' }}>
        <h3>Previewed Sessions & Bonuses</h3>
        {paidSessions.length === 0 ? (
          <p>No sessions or bonuses found.</p>
        ) : (
          <table border="1" cellPadding="5" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Date of Service</th>
                <th>Client / Description</th>
                <th>Service</th>
                <th>Billed</th>
                <th>Collected</th>
              </tr>
            </thead>
            <tbody>
              {paidSessions.map((s, index) => (
                <tr key={index}>
                  <td>
                    {s.session_date
                      ? new Date(s.session_date).toLocaleDateString()
                      : ''}
                  </td>
                  <td>{s.client_name || ''}</td>
                  <td>{s.service_type || ''}</td>
                  <td>${s.billed_amount || 0}</td>
                  <td>${s.collected_amount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* EXISTING PAYROLL SUMMARIES */}
      <h3 style={{ marginTop: '2rem' }}>Existing Payroll Summaries</h3>
      <table border="1" cellPadding="5" style={{ marginTop: '1rem' }}>
        <thead>
          <tr>
            <th>Therapist</th>
            <th>Start</th>
            <th>End</th>
            <th>Total Collected</th>
            <th>Therapist Pay</th>
            <th>Approved By</th>
            <th>Approved At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payrolls.map((p) => (
            <tr key={p.id}>
              <td>
                {p.first_name} {p.last_name}
              </td>
              <td>{p.start_date}</td>
              <td>{p.end_date}</td>
              <td>${p.total_collected}</td>
              <td>
                ${parseFloat(p.therapist_pay || '0').toFixed(2)}
              </td>
              <td>{p.approved_by || 'N/A'}</td>
              <td>
                {p.approved_at ? new Date(p.approved_at).toLocaleString() : 'N/A'}
              </td>
              <td>
                {!p.approved_at && (
                  <button onClick={() => approvePayroll(p.id)}>Approve</button>
                )}{' '}
                <button onClick={() => deletePayroll(p.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminPayrollPage;
